import { AsyncPipe, DatePipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnDestroy,
    OnInit,
    Signal,
    signal
} from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ButtonComponent } from "@components/button/button.component";
import {
    firstValueFrom,
    merge,
    Observable,
    of,
    Subject
} from "rxjs";
import {
    catchError,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap
} from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { EventService } from "~app/common/services/cqs/event.service";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage } from "~app/common/services/cqs/models/rule-validation-message";
import { Utils } from "~app/common/services/utils";
import { Kontrakt } from "~app/features/virkeskop/models/kontrakt-models";
import { AvtalMediatorService } from "~app/features/virkeskop/services/avtal-mediator.service";
import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { DigitalSigneringMediatorService } from "~app/features/virkeskop/services/digitalsignering-mediator.service";
import { KontraktDokumentService } from "~app/features/virkeskop/services/kontrakt-dokument.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { UtskriftMediatorService } from "~app/features/virkeskop/services/utskrift-mediator.service";
import { canEdit } from "~app/role-permissions";
import { CreateRapportutskriftRequestDTO, CreateRapportutskriftResponseDTO, UtskriftTyp, VKKontraktUtskriftDTO } from "../../../models/utskrift/utskrift-models";
import { VirkeskopValidationService } from "../../../services/validation/virkeskop-validation.service";
import { VirkeskopValidationIconPopoverComponent } from "../../../validation/validation-icon-popover/virkeskop-validation-icon-popover.component";
import { RapportUtskriftCreatedEventMessage } from "./rapportUtskriftCreatedEventMessage";
interface IUtskriftsRapportObjekt {
  buttonText: string;
  entityId?: number;
  includeBetalplan: boolean;
  includeKartor: boolean;
  includeKontrakt: boolean;
  includeSkogsvardsKontrakt: boolean;
  includeVardeberakning: boolean;
  includesSkogsvardsSammanstallning: boolean;
  includeSkogsvardsKartor: boolean;
  rubrik: string;
  staticWidthButton: boolean;
}

@Component({
    selector: "rask-kontrakt-generera-rapport",
    templateUrl: "./kontrakt-generera-rapport.component.html",
    styleUrls: ["./kontrakt-generera-rapport.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        NgClass,
        VirkeskopValidationIconPopoverComponent,
        DatePipe,
        AsyncPipe
    ]
})
export class KontraktGenereraRapportComponent implements OnInit, OnDestroy {
    entityId = input<number>();
    rapportTyp = input<UtskriftTyp>();

    protected readonly errorContext = RuleErrorContext.rapportutskrift;
    protected isVisible$: Observable<boolean>;
    protected kontraktId: number;
    protected loading = signal(true);
    protected skapaUtskrifterDisabled = computed(() => this.loading()
        || this.avtalDirty()
        || this.hasErrors()
        || this.isReadonly()
        || !this.canEdit());
    protected utskriftsRapport = computed(() => this.getUtskriftsRapport());
    protected utskrift$: Observable<VKKontraktUtskriftDTO>;
    protected readonly avtalDirty: Signal<boolean>;
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly avtalService = inject(AvtalService);
    private readonly digitalSigneringMediatorService = inject(DigitalSigneringMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly kontraktDokumentService = inject(KontraktDokumentService);
    private readonly eventService = inject(EventService);
    private readonly createUtskriftSrc = new Subject<CreateRapportutskriftRequestDTO>();
    private readonly isReadonly = toSignal(this.kontraktMediatorService.isReadonly$);

    private readonly canEdit = canEdit();

    private readonly hasErrors = toSignal(this.validationService.visibleMessages$.pipe(
        map(messages => messages.filter(x =>
            x.severity === RuleSeverityLevel.error && x.context === this.errorContext
        ).length > 0)
    ));

    private readonly createUtskrift$: Observable<CreateRapportutskriftResponseDTO> = this.createUtskriftSrc.pipe(
        tap(() => this.loading.set(true)),
        switchMap(options => this.avtalService.createKontraktUtskrifter(this.kontraktId, options).pipe(
            catchError(() => {
                this.loading.set(false);
                return of({
                    validationMessages: []
                });
            })
        )),
        tap(response => this.displayValidationMessages(response.validationMessages)),
        takeUntilDestroyed()
    );

    private readonly entityId$ = toObservable(this.entityId);

    private kontrakt: Kontrakt;

    constructor() {
        this.avtalDirty = toSignal(this.avtalMediatorService.dirty$);
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        const rapportUtskriftCreatedEvent$ = this.eventService.subscribeEvent<RapportUtskriftCreatedEventMessage>(RapportUtskriftCreatedEventMessage.TYPE).pipe(
            filter(message => message.dokumenttyper.includes(this.rapportTyp()) && message.kontraktId === this.kontraktId),
            tap(message => {
                if (!message.success) {
                    this.raskModalService.error("Fel", {
                        message: `Något gick fel vid skapandet av utskrift för '${this.rapportTyp()}'. Försök igen senare eller kontakta support om problemet kvarstår.`,
                        messageType: "UserMessage"
                    });
                    this.loading.set(false);
                }
            }),
            filter(message => message.success)
        );

        this.utskrift$ = merge(rapportUtskriftCreatedEvent$, this.entityId$).pipe(
            switchMap(() => this.kontraktMediatorService.kontrakt$.pipe(take(1))),
            switchMap((kontrakt) =>
                this.avtalService.getDokumentInfo({
                    parentId: kontrakt.id,
                    dokumentTyper: [this.rapportTyp()]
                })
            ),
            map((utskrifter) => this.filtreraUtskrift(utskrifter)),
            tap(() => this.loading.set(false))
        );

        this.createUtskrift$.subscribe();

        this.kontraktMediatorService.kontrakt$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((kontrakt) => {
                this.kontrakt = kontrakt;
                this.kontraktId = kontrakt.id;
            });
    }

    openDokument(utskrift: VKKontraktUtskriftDTO) {
        this.kontraktDokumentService
            .getDokumentForKontraktDownload(this.kontraktId, utskrift.id)
            .subscribe((blob: Blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url);
            });
        return false;
    }

    downloadDokument(utskrift: VKKontraktUtskriftDTO) {
        this.kontraktDokumentService
            .getDokumentForKontraktDownload(this.kontraktId, utskrift.id)
            .subscribe((blob: Blob) => {
                Utils.downloadBlob(blob, utskrift.dokumentNamn);
            });
        return false;
    }

    protected async onSkapaUtskrifter() {
        const confirmed = await this.confirmAktivDigitalSignering();
        if (confirmed === ConfirmDialogResponse.cancel) {
            return;
        }

        const includeKontraktsbelopp = this.kontrakt.isRotpost
            || this.utskriftMediatorService.inkluderaBeloppIUtskrift;

        const options: CreateRapportutskriftRequestDTO = {
            ...this.utskriftsRapport(),
            entityId: this.entityId(),
            includeKontraktsbelopp
        };

        this.createUtskriftSrc.next(options);
    }

    private getUtskriftsRapport(): IUtskriftsRapportObjekt {
        const utskriftsRapport: IUtskriftsRapportObjekt = {
            includeKartor: false,
            includeKontrakt: false,
            includeSkogsvardsKontrakt: false,
            includeVardeberakning: false,
            includeBetalplan: false,
            includesSkogsvardsSammanstallning: false,
            includeSkogsvardsKartor: false,
            buttonText: "",
            rubrik: "",
            staticWidthButton: true
        };

        switch (this.rapportTyp()) {
            case "Kontrakt":
                utskriftsRapport.rubrik = "Kontrakt";
                utskriftsRapport.buttonText = "Skapa kontrakt";
                utskriftsRapport.includeKontrakt = true;
                break;

            case "Kontrakt-karta":
                utskriftsRapport.rubrik = "Karta";
                utskriftsRapport.buttonText = "Skapa karta";
                utskriftsRapport.includeKartor = true;
                break;

            case "Kontrakt-vardeberakning":
                utskriftsRapport.rubrik = "Värdeberäkning";
                utskriftsRapport.buttonText = "Skapa värdeberäkning";
                utskriftsRapport.includeVardeberakning = true;
                break;

            case "SkogsvardsKontrakt":
                utskriftsRapport.rubrik = "Skogsvårdskontrakt";
                utskriftsRapport.buttonText = "Skapa skogsvårdskontrakt";
                utskriftsRapport.includeSkogsvardsKontrakt = true;
                utskriftsRapport.staticWidthButton = true;
                break;
            case "Kontrakt-betalplan":
                utskriftsRapport.rubrik = "Betalplan";
                utskriftsRapport.buttonText = "Skapa betalplan";
                utskriftsRapport.includeBetalplan = true;
                break;
            case "Kontrakt-skogsvardsSammanstallning":
                utskriftsRapport.rubrik = "Skogsvårds sammanställning";
                utskriftsRapport.buttonText = "Skapa";
                utskriftsRapport.includesSkogsvardsSammanstallning = true;
                break;
            case "Kontrakt-skogsvardsKarta":
                utskriftsRapport.rubrik = "Skogsvårdskarta";
                utskriftsRapport.buttonText = "Skapa skogsvårdskarta";
                utskriftsRapport.includeSkogsvardsKartor = true;
                break;
            default:
        }

        return utskriftsRapport;
    }

    private filtreraUtskrift(
        utskrifter: VKKontraktUtskriftDTO[]
    ): VKKontraktUtskriftDTO {
        const isBetalplan = this.rapportTyp() === "Kontrakt-betalplan";
        if (isBetalplan && this.entityId()) {
            return utskrifter?.find(
                (x) =>
                    x.typ === this.rapportTyp() &&
          x.virkeskopskontraktSubEntityId === this.entityId()
            );
        }
        return utskrifter?.find(x => x.typ === this.rapportTyp());
    }

    private async confirmAktivDigitalSignering() {
        if (this.rapportTyp() === "Kontrakt-vardeberakning") {
            return true;
        }

        const hasAktivSignering = await firstValueFrom(this.digitalSigneringMediatorService.hasAktivSignering$);
        if (!hasAktivSignering) {
            return true;
        }

        const modalResult$ = this.raskModalService.confirm(new ModalSettings("Varning",
            `Det finns aktiva digitala signeringar. Är du säker på att du vill skapa om '${this.utskriftsRapport().rubrik}'?`));
        return firstValueFrom(modalResult$);
    }

    private displayValidationMessages(messages: RuleValidationMessage[]) {
        if (messages?.length > 0) {
            this.loading.set(false);
        }

        messages.forEach(message => {
            this.validationService.updateClientSideValidation(true, message);
        });
    }
}
